import {
  booleanAttribute,
  Component,
  computed,
  inject,
  InjectionToken,
  input,
  output,
  signal,
} from '@angular/core';

export type ImageCrossorigin = 'anonymous' | 'use-credentials' | '' | null;

export interface OcpImageDefaults {
  crossorigin: ImageCrossorigin;
}

export const OCP_IMAGE_DEFAULTS = new InjectionToken<OcpImageDefaults>('OCP_IMAGE_DEFAULTS', {
  factory: () => ({ crossorigin: 'anonymous' }),
});

@Component({
  selector: 'ocp-image',
  styles: `
    :host {
      display: flex;
    }
  `,
  template: `
    @if (effectiveSrc()) {
      @if (useOptimizedImage()) {
        <img
          [class]="imgClass()"
          [src]="effectiveSrc()!"
          [alt]="alt()"
          [width]="width()"
          [height]="height()"
          [attr.fill]="fill()"
          [attr.loading]="loading() ?? null"
          [attr.decoding]="decoding() ?? null"
          [attr.fetchpriority]="priority() ? 'high' : null"
          [attr.srcset]="srcset() || null"
          [attr.sizes]="sizes() || null"
          [attr.crossorigin]="resolvedCrossorigin()"
          [attr.referrerpolicy]="referrerPolicy() ?? null"
          [attr.role]="role() || null"
          [attr.aria-label]="ariaLabel() || null"
          [attr.aria-hidden]="ariaHidden()"
          (error)="onImageError($event)"
        />
      } @else {
        <img
          [class]="imgClass()"
          [src]="effectiveSrc()!"
          [alt]="alt()"
          [width]="width()"
          [height]="height()"
          [attr.loading]="loading() ?? null"
          [attr.decoding]="decoding() ?? null"
          [attr.fetchpriority]="priority() ? 'high' : null"
          [attr.srcset]="srcset() || null"
          [attr.sizes]="sizes() || null"
          [attr.crossorigin]="resolvedCrossorigin()"
          [attr.referrerpolicy]="referrerPolicy() ?? null"
          [attr.role]="role() || null"
          [attr.aria-label]="ariaLabel() || null"
          [attr.aria-hidden]="ariaHidden()"
          (error)="onImageError($event)"
        />
      }
    }
  `,
})
export class OcpImageComponent {
  readonly src = input<string | null | undefined>(undefined);
  readonly fallbackSrc = input<string | null | undefined>(undefined);
  readonly alt = input<string>('');
  readonly imgClass = input<string>('');

  readonly optimized = input(false, { transform: booleanAttribute });
  readonly fill = input(false, { transform: booleanAttribute });
  readonly priority = input(false, { transform: booleanAttribute });

  readonly width = input<number | string | null | undefined>(undefined);
  readonly height = input<number | string | null | undefined>(undefined);
  readonly loading = input<'lazy' | 'eager' | 'auto' | undefined>(undefined);
  readonly decoding = input<'sync' | 'async' | 'auto' | undefined>(undefined);
  readonly srcset = input<string | undefined>(undefined);
  readonly sizes = input<string | undefined>(undefined);

  readonly crossorigin = input<ImageCrossorigin | undefined>(undefined);
  readonly referrerPolicy = input<string | null | undefined>(undefined);

  readonly role = input<string>('');
  readonly ariaLabel = input<string>('');
  readonly ariaHidden = input<string | null | undefined>(undefined);

  readonly imageError = output<Event>();

  private readonly defaults = inject(OCP_IMAGE_DEFAULTS);
  private readonly failedPrimarySrc = signal<string | null>(null);

  protected readonly effectiveSrc = computed(() => {
    const primarySrc = this.src();
    const fallbackSrc = this.fallbackSrc();
    const shouldUseFallback =
      !!primarySrc && !!fallbackSrc && this.failedPrimarySrc() === primarySrc;

    return shouldUseFallback ? fallbackSrc : primarySrc;
  });

  protected readonly resolvedCrossorigin = computed(() =>
    this.crossorigin() === undefined ? this.defaults.crossorigin : this.crossorigin()
  );

  protected readonly useOptimizedImage = computed(() => {
    const source = this.effectiveSrc();
    if (!source) {
      return false;
    }

    return this.optimized() && !source.startsWith('data:');
  });

  protected onImageError(event: Event): void {
    this.imageError.emit(event);

    const primarySrc = this.src();

    if (!primarySrc || !this.fallbackSrc()) {
      return;
    }

    if (this.effectiveSrc() === primarySrc) {
      this.failedPrimarySrc.set(primarySrc);
    }
  }
}
